import { useState } from 'react';
import axios from 'axios';
import jsPDF from 'jspdf';
import { Sparkles, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;

/* AI-generated, app-themed Brand Standards downloader.

   Calls POST /api/admin/ai/brand-standards which uses the Emergent LLM Key
   (GPT-5.2) to write the prose. The deterministic palette + property title
   come from site_settings so the AI can never invent off-brand colors. The
   resulting JSON is rendered to a multi-page PDF here on the client using
   jsPDF — Hyatt blue cover, swatch grid, dos/don'ts blocks. */
export default function BrandStandardsButton() {
  const adminToken = typeof window !== 'undefined' ? localStorage.getItem('admin_token') : null;
  const [busy, setBusy] = useState(false);

  if (!adminToken) return null; // generation requires admin/editor

  const generate = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/ai/brand-standards`,
        {},
        { headers: { Authorization: `Bearer ${adminToken}` }, withCredentials: true, timeout: 120000 },
      );
      await renderPdf(data);
      toast.success('Brand Standards downloaded');
    } catch (err) {
      const detail = err.response?.data?.detail || err.message || 'Generation failed';
      toast.error(`Brand Standards: ${detail}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      type="button"
      onClick={generate}
      disabled={busy}
      className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-bold transition-all hover:-translate-y-0.5 disabled:opacity-60"
      style={{
        background: 'linear-gradient(135deg, #003DA5, #1F4FBE)',
        color: 'white',
        fontFamily: 'Manrope',
        boxShadow: '0 2px 8px rgba(0,61,165,0.3)',
      }}
      data-testid="brand-standards-generate"
      title="Generate AI-formatted Brand Standards PDF"
    >
      {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
      {busy ? 'Generating…' : 'Download Brand Standards (AI)'}
    </button>
  );
}

// ───────────────────────── PDF rendering ─────────────────────────
// Helper to load an image URL as a data URL so jsPDF can embed it.
const fetchAsDataUrl = async (url) => {
  if (!url) return null;
  try {
    const abs = url.startsWith('http') ? url : (url.startsWith('/api/') ? `${BACKEND}${url}` : `${typeof window !== 'undefined' ? window.location.origin : ''}${url}`);
    const r = await fetch(abs, { mode: 'cors', credentials: 'omit' });
    if (!r.ok) return null;
    const blob = await r.blob();
    if (!blob.type.startsWith('image/')) return null;
    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch { return null; }
};

const hexToRgb = (hex) => {
  const m = (hex || '').replace('#', '').match(/^([0-9a-f]{6})$/i);
  if (!m) return [0, 0, 0];
  const v = m[1];
  return [parseInt(v.slice(0, 2), 16), parseInt(v.slice(2, 4), 16), parseInt(v.slice(4, 6), 16)];
};

async function renderPdf(doc) {
  const pdf = new jsPDF({ unit: 'pt', format: 'a4' });
  const W = pdf.internal.pageSize.getWidth();
  const H = pdf.internal.pageSize.getHeight();
  const M = 48;
  const PRIMARY = doc.swatches?.[0]?.hex || '#003DA5';
  const ACCENT = doc.swatches?.[1]?.hex || '#FF6F59';
  const STONE = doc.swatches?.[2]?.hex || '#F4F1EB';

  const logoData = await fetchAsDataUrl(doc.logo_url);

  // ── COVER PAGE ──
  const [pr, pg, pb] = hexToRgb(PRIMARY);
  pdf.setFillColor(pr, pg, pb);
  pdf.rect(0, 0, W, H, 'F');
  // Decorative accent stripe at top
  const [ar, ag, ab] = hexToRgb(ACCENT);
  pdf.setFillColor(ar, ag, ab);
  pdf.rect(0, 0, W, 8, 'F');

  if (logoData) {
    try { pdf.addImage(logoData, 'JPEG', M, M, 130, 80); } catch { /* not jpeg, skip */ }
  }

  // Cover title
  pdf.setTextColor(255, 255, 255);
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(11);
  pdf.text('BRAND STANDARDS', M, H * 0.45, { charSpace: 4 });
  pdf.setFontSize(40);
  pdf.text(doc.title || 'Brand Standards', M, H * 0.5 + 6, { maxWidth: W - 2 * M });
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(13);
  pdf.text(doc.subtitle || '', M, H * 0.5 + 56, { maxWidth: W - 2 * M });

  // Tagline
  if (doc.tagline) {
    pdf.setFont('helvetica', 'italic');
    pdf.setFontSize(15);
    pdf.text(`"${doc.tagline}"`, M, H * 0.7, { maxWidth: W - 2 * M });
  }

  // Footer date
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(9);
  const date = doc.generated_at ? new Date(doc.generated_at).toLocaleDateString(undefined, { year: 'numeric', month: 'long' }) : '';
  pdf.text(`Edition · ${date}`, M, H - M);

  // ── PHILOSOPHY PAGE ──
  const newSection = (heading) => {
    pdf.addPage();
    // Cream background
    const [sr, sg, sb] = hexToRgb(STONE);
    pdf.setFillColor(sr, sg, sb);
    pdf.rect(0, 0, W, H, 'F');
    // Header band
    pdf.setFillColor(pr, pg, pb);
    pdf.rect(0, 0, W, 60, 'F');
    if (logoData) {
      try { pdf.addImage(logoData, 'JPEG', W - 100 - 24, 12, 100, 36); } catch { /* skip */ }
    }
    pdf.setTextColor(255, 255, 255);
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(8);
    pdf.text((doc.title || '').toUpperCase(), M, 26, { charSpace: 3 });
    pdf.setFontSize(20);
    pdf.text(heading, M, 48);
    pdf.setTextColor(26, 46, 46); // #1A2E2E
  };

  // Wrap-text helper
  const wrappedText = (txt, x, y, maxW, lineHeight = 14, fontSize = 10) => {
    pdf.setFontSize(fontSize);
    pdf.setFont('helvetica', 'normal');
    const lines = pdf.splitTextToSize(txt, maxW);
    let cy = y;
    for (const ln of lines) {
      if (cy > H - M) { pdf.addPage(); cy = M + 20; }
      pdf.text(ln, x, cy);
      cy += lineHeight;
    }
    return cy;
  };

  if (doc.philosophy) {
    newSection('Philosophy');
    let y = 100;
    if (doc.tagline) {
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(14);
      pdf.setTextColor(pr, pg, pb);
      pdf.text(doc.tagline, M, y, { maxWidth: W - 2 * M });
      y += 28;
      pdf.setTextColor(26, 46, 46);
    }
    y = wrappedText(doc.philosophy, M, y, W - 2 * M, 16, 11);
    if (doc.voice) {
      y += 18;
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(12);
      pdf.setTextColor(pr, pg, pb);
      pdf.text('Voice', M, y);
      y += 16;
      pdf.setTextColor(26, 46, 46);
      wrappedText(doc.voice, M, y, W - 2 * M, 16, 11);
    }
  }

  // ── COLOR PALETTE PAGE ──
  newSection('Color Palette');
  let y = 100;
  const cardW = (W - 2 * M - 24) / 2;
  const cardH = 96;
  const swatches = doc.swatches || [];
  swatches.forEach((s, i) => {
    const col = i % 2;
    const row = Math.floor(i / 2);
    const x = M + col * (cardW + 24);
    const yy = y + row * (cardH + 16);
    // Color block
    const [r, g, b] = hexToRgb(s.hex);
    pdf.setFillColor(r, g, b);
    pdf.roundedRect(x, yy, 80, cardH, 4, 4, 'F');
    // Card body
    pdf.setFillColor(255, 255, 255);
    pdf.roundedRect(x + 88, yy, cardW - 88, cardH, 4, 4, 'F');
    pdf.setTextColor(26, 46, 46);
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(11);
    pdf.text(s.name, x + 100, yy + 22);
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(9);
    pdf.setTextColor(122, 140, 140);
    pdf.text(s.hex.toUpperCase(), x + 100, yy + 38);
    pdf.setTextColor(26, 46, 46);
    pdf.setFontSize(9);
    const usageLines = pdf.splitTextToSize(s.usage || '', cardW - 100);
    pdf.text(usageLines, x + 100, yy + 56);
  });

  // ── SECTIONS WITH DOS/DONTS ──
  for (const section of (doc.sections || [])) {
    newSection(section.heading);
    let yy = 100;
    if (section.body) {
      yy = wrappedText(section.body, M, yy, W - 2 * M, 16, 11);
      yy += 14;
    }
    // Two columns: dos | donts
    const colW = (W - 2 * M - 18) / 2;
    const renderList = (items, x, headerLabel, color) => {
      const [hr, hg, hb] = hexToRgb(color);
      pdf.setFillColor(hr, hg, hb);
      pdf.roundedRect(x, yy, colW, 22, 4, 4, 'F');
      pdf.setTextColor(255, 255, 255);
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(9);
      pdf.text(headerLabel, x + 10, yy + 14);
      let by = yy + 38;
      pdf.setTextColor(26, 46, 46);
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(10);
      for (const item of items || []) {
        const lines = pdf.splitTextToSize(`• ${item}`, colW - 14);
        if (by + lines.length * 14 > H - M) { pdf.addPage(); by = M + 20; }
        pdf.text(lines, x + 7, by);
        by += lines.length * 14 + 4;
      }
      return by;
    };
    const leftEnd  = renderList(section.dos,   M,                'DOS',   '#16A34A');
    const rightEnd = renderList(section.donts, M + colW + 18,    "DON'TS", '#DC2626');
    yy = Math.max(leftEnd, rightEnd);
  }

  // ── PAGE NUMBERS ──
  const pageCount = pdf.internal.getNumberOfPages();
  for (let p = 1; p <= pageCount; p++) {
    pdf.setPage(p);
    pdf.setTextColor(122, 140, 140);
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(8);
    if (p > 1) pdf.text(`${p} / ${pageCount}`, W - M, H - 24, { align: 'right' });
  }

  const fname = `brand-standards-${(doc.title || 'document').replace(/[^A-Za-z0-9-]+/g, '-').toLowerCase()}.pdf`;
  pdf.save(fname);
}
